<script lang="ts" setup>
import { ref, watch } from 'vue';
import { type TagDetails } from '@precon-tools-suite-by-shared/shared-tags-data-access';
import { TagDisplay } from '@precon-tools-suite-by-shared/shared-tags-ui';
import { useCurrentTeamsTags } from '../composables/useCurrentTeamsTags';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';

const props = withDefaults(
    defineProps<{
        modelValue: string[];
    }>(),
    { modelValue: () => [] }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void;
}>();

// ------------------------------------------------------------------------------------------------------------------ //
//  STATE                                                                                                             //
// ------------------------------------------------------------------------------------------------------------------ //

const internalTags = ref<TagDetails[]>([]);

// ------------------------------------------------------------------------------------------------------------------ //
//  DATA RETRIEVAL                                                                                                    //
// ------------------------------------------------------------------------------------------------------------------ //

const { nodes: suggestions, searchQuery, searching } = useCurrentTeamsTags();

// ------------------------------------------------------------------------------------------------------------------ //
//  MAPPING FUNCTIONS                                                                                                 //
// ------------------------------------------------------------------------------------------------------------------ //

const tagToString = (tag: TagDetails): string => tag.id;
const stringToTag = (id: string): TagDetails | undefined =>
    internalTags.value.find((tag) => tag.id === id);

// ------------------------------------------------------------------------------------------------------------------ //
//  WATCHERS                                                                                                          //
// ------------------------------------------------------------------------------------------------------------------ //

watch(
    () => props.modelValue,
    (newValue) => {
        internalTags.value = newValue
            .map(stringToTag)
            .filter((tag): tag is TagDetails => tag !== undefined);
    },
    { immediate: true }
);

watch(
    () => internalTags.value,
    (newTags) => {
        const newModelValue = newTags.map(tagToString);
        if (newModelValue.join() !== props.modelValue.join()) {
            emit('update:modelValue', newModelValue);
        }
    }
);

// ------------------------------------------------------------------------------------------------------------------ //
//  ACTIONS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

const onSearch = (event: AutoCompleteCompleteEvent) => {
    searchQuery.value = event.query;
};

const onModelValueUpdate = (tags: TagDetails[]) => {
    internalTags.value = tags;
};

const onClickClose = (tag: TagDetails) => {
    internalTags.value = internalTags.value.filter((x) => x.id !== tag.id);
};
</script>

<template>
    <AutoComplete
        :model-value="internalTags"
        :suggestions="suggestions"
        multiple
        :loading="searching"
        :pt="{
            removeTokenIcon: {
                style: 'display: none;',
            },
            token: {
                style: 'padding: 0;',
            },
        }"
        @update:model-value="onModelValueUpdate"
        @complete="onSearch"
    >
        <template #chip="{ value }">
            <TagDisplay
                :node="value"
                closable
                @close:click="onClickClose(value)"
            />
        </template>
        <template #option="{ option }">
            <TagDisplay :node="option" />
        </template>
    </AutoComplete>
</template>
